import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
//import Aura from '@primeuix/themes/aura';
//import Material from '@primeuix/themes/material';
//import Lara from '@primeuix/themes/lara';
//import Nora from '@primeuix/themes/nora';
import { definePreset } from '@primeuix/themes';
import './sync/df-ajax';
import './request-iframe-sizes';
import { includeStylesheet, setCssVariables } from './common';
import * as dh from './dh-colors';
import { setupErrorHandler } from './error-handler';

const params = new URLSearchParams(window.location.search);
let admin = params.get('admin');
admin = (admin == '1' || admin == 'true');

setupErrorHandler();

window.DRF = window.DRF || {};
window.DRF.drfuhrmanSite = import.meta.env.DRFUHRMAN_SITE;

const app = createApp(App);

//console.log({
//    material: (await import('@primeuix/themes/material')).default,
//    aura: (await import('@primeuix/themes/aura')).default,
//});

// Admin Colors
const ac = {
    // Taken from Quasar
    textColor: 'rgba(0, 0, 0, 0.87)',
    blue500: "#2196F3",
    blue700: "#1976D2",
    red600: "#E53935",
    red700: "#D32F2F",
    slate200: "#e2e8f0",
    slate300: "#cbd5e1",
    slate400: "#94a3b8",
    slate500: "#64748b",
    slate700: "#334155",
};

// Choices include: Aura, Material, Lara, Nora
const themePreset = (admin? definePreset((await import('@primeuix/themes/material')).default, {
    components: {
        badge: {
            root: {
                borderRadius: '{border.radius.xl}',
            },
        },
        card: {
            // Material theme doubles the card body gap and padding vs Aura,
            // so halve it back agan.
            body: {
                gap: '0.5rem',
                padding: '1.25rem',
            },
        },
        datatable: {
            rowToggleButton: {
                size: '2.625rem', // default is 1.75rem
            },
        },
    },
    semantic: {
        colorScheme: {
            light: {
                primary: {
                    activeColor: "{primary.300}", // was 300
                    color: "{primary.700}", // was 500
                    contrastColor: "#ffffff", // was white
                    hoverColor: "{primary.500}", // was 400
                },
                text: {
                    color: ac.textColor,
                },
                overlay: {
                    popover: {
                        // needs to be pretty dark, so use the text color
                        borderColor: ac.textColor,
                    },
                },
            },
        },
        primary: {
            50: "{blue.50}",
            100: "{blue.100}",
            200: "{blue.200}",
            300: "{blue.300}",
            400: "{blue.400}",
            500: "{blue.500}",
            600: "{blue.600}",
            700: "{blue.700}",
            800: "{blue.800}",
            900: "{blue.900}",
            950: "{blue.950}",
        },
    },
}) : definePreset((await import('@primeuix/themes/aura')).default, {
    components: {
        button: {
            colorScheme: {
                light: {
                    outlined: {
                        primary: {
                            "hoverBackground": dh.colorLightBlue,
                            "activeBackground": dh.colorLightTeal,
                            "borderColor": dh.colorMediumGrey,
                            "color": "{primary.color}"
                        },
                    },
                },
            },
        },
        progressspinner: {
            colorScheme: {
                light: {
                    root: {
                        colorOne: "{gray.200}",
                        colorTwo: "{gray.400}",
                        colorThree: "{gray.600}",
                        colorFour: "{gray.800}",
                    },
                },
            },
        },
    },
    semantic: {
        colorScheme: {
            light: {
                primary: {
                    "color": dh.colorDarkBlue, // 500
                    "contrastColor": "#ffffff",
                    "hoverColor": dh.colorDarkTeal, // 600
                    "activeColor": dh.colorMediumBlue, // 700
                },
                text: {
                    color: dh.colorDarkBlue,
                    //hoverColor: "{surface.800}",
                    //mutedColor: "{surface.500}",
                    //hoverMutedColor: "{surface.600}"
                },
                overlay: {
                    popover: {
                        // normal border in the style guide is #D9D9D9
                        // but let's go darker
                        borderColor: dh.colorDarkBlue,
                    },
                },
            },
        },
        transitionDuration: '0.3s',
    },
}));

if (admin) {
    includeStylesheet({ href: 'https://fonts.googleapis.com/css2?family=Roboto+Mono:ital,wght@0,100..700;1,100..700&family=Roboto:ital,wght@0,100..900;1,100..900&display=swap' });
    setCssVariables('drf', {
        text: {
            color: ac.textColor,
        },
        heading: {
            'font-family': 'Roboto, -apple-system, Helvetica Neue, Helvetica, Arial, sans-serif',
            'letter-spacing': 'normal',
        },
        body: {
            'font-family': 'Roboto, -apple-system, Helvetica Neue, Helvetica, Arial, sans-serif',
            'letter-spacing': 'normal',
        },
        body2: {
            'font-weight': 400,
        },
        border: {
            // e2e2e2 was grabbed from a screenshot of Quasar
            // the Material theme seems to like slate 300, which is "#cbd5e1"
            color: ac.slate300,
        },
        link: {
            'color': ac.blue700,
            'hover-color': ac.blue500,
            'active-color': ac.blue700,
        },
        icon: {
            'color': ac.textColor,
            'hover-color': ac.slate500,
            'active-color': ac.slate400,
        },
        removeicon: {
            'color': ac.textColor,
            'hover-color': ac.red600,
            'active-color': ac.slate400,
        },
        addpm: {
            'color': ac.textColor,
            'bgcolor': 'white',
            'hover-color': ac.textColor,
            'hover-bgcolor': ac.slate200,
            'active-color': ac.slate500,
            'active-bgcolor': ac.slate200,
        },
    });
} else {
    includeStylesheet({ href: 'https://use.typekit.net/jee4rlv.css' });
    setCssVariables('drf', {
        text: {
            color: dh.colorDarkBlue,
        },
        heading: {
            'font-family': 'p22-mackinac-pro',
            'letter-spacing': 'normal',
        },
        body: {
            'font-family': 'neue-haas-grotesk-display, sans-serif',
            'letter-spacing': '0.05em',
        },
        body2: {
            'font-weight': 500,
        },
        border: {
            color: dh.colorMediumGrey,
        },
        link: {
            'color': dh.colorDarkGreen,
            'hover-color': dh.colorDarkTeal,
            'active-color': dh.colorDarkGreen,
        },
        icon: {
            'color': dh.colorDarkBlue,
            'hover-color': dh.colorDarkTeal,
            'active-color': dh.colorMediumBlue,
        },
        removeicon: {
            'color': dh.colorDarkBlue,
            'hover-color': dh.colorRed,
            'active-color': dh.colorMediumBlue, // was dark teal, but changed to medium blue to be more similar to regular icons, also buttons
        },
        addpm: {
            'color': dh.colorDarkBlue,
            'bgcolor': 'white',
            'hover-color': dh.colorDarkBlue,
            'hover-bgcolor': dh.colorLightBlue,
            'active-color': dh.colorDarkTeal,
            'active-bgcolor': dh.colorLightBlue,
        },
    });
}

app.use(PrimeVue, {
    ripple: !!admin,
    theme: {
        preset: themePreset,
        options: {
            darkModeSelector: false,
        },
    },
    pt: (admin? {
        button: {
            root: {
                class: 'p-ripple',
                style: 'text-transform:uppercase;'
            },
        },
        tag: {
            root: 'p-tag p-tag-rounded',
        },
    } : {
        button: {
            root: 'p-button p-button-rounded',
        },
    }),
});

app.directive('tooltip', Tooltip);

app.use(createPinia());

app.mount('#app');
